import type { CSSProperties, PropsWithChildren, RefObject } from "react";
import React, {
    useState,
    useRef,
    useEffect,
    forwardRef,
    useImperativeHandle,
    useCallback,
} from "react";

import cn from "classnames";

import { extractProps } from "../../../utils/react";

import type { BaseViews, PropsWithBase } from "../../../types/react";

import classes from "./ScrollContainer.styl";
import ScrollButtonCustom from "../../../assets/common/scrollButtonCustom.svg";

export type ScrollContainerView = BaseViews | "secondary" | "base";

export interface ScrollContainerRef {
    scrollTo: (position: number, withAnimation?: boolean) => void;
}

interface IScrollContainerBaseProps {
    buttonRef: RefObject<ScrollContainerRef>;
    offset?: number;
    labelLeft?: string;
    labelRight?: string;
    view?: ScrollContainerView;
    onLeftClick?: () => void;
    onRightClick?: () => void;
}

export type ScrollContainerProps = PropsWithChildren<
    PropsWithBase<IScrollContainerBaseProps, ScrollContainerView>
>;

const MOVEMENT_ANIMATION_DURATION = 500;

const ScrollContainer = forwardRef<ScrollContainerRef, ScrollContainerProps>(
    (props, ref) => {
        const {
            offset = 200,
            children,
            labelLeft = "Scroll Left",
            labelRight = "Scroll Right",
            view = "primary",
            onLeftClick,
            onRightClick,
        } = props;

        const { customColorsStyles } = extractProps(props, [
            "customColorsStyles",
        ]);

        const wrapperRef = useRef<HTMLDivElement>(null);
        const containerRef = useRef<HTMLDivElement>(null);

        const [offsetX, setOffsetX] = useState(0);
        const [isMovementAnimating, setIsMovementAnimating] = useState(false);
        const [wrapperWidth, setWrapperWidth] = useState(0);
        const [containerWidth, setContainerWidth] = useState(0);

        const maxContainerOffset = containerWidth - wrapperWidth;

        const changeOffsetX = useCallback(
            (delta: number, withAnimation = true) => {
                if (withAnimation) {
                    setIsMovementAnimating(true);
                }

                setOffsetX((prev) =>
                    Math.max(0, Math.min(prev + delta, maxContainerOffset)),
                );

                if (!withAnimation) {
                    setIsMovementAnimating(false);
                }
            },
            [maxContainerOffset],
        );

        const scrollTo = useCallback(
            (position: number, withAnimation = true) => {
                if (withAnimation) {
                    setIsMovementAnimating(true);
                }

                setOffsetX(Math.max(0, Math.min(position, maxContainerOffset)));

                if (!withAnimation) {
                    setIsMovementAnimating(false);
                }
            },
            [maxContainerOffset],
        );

        useImperativeHandle(ref, () => ({
            scrollTo,
        }));

        useEffect(() => {
            if (isMovementAnimating) {
                const timer = setTimeout(
                    () => setIsMovementAnimating(false),
                    MOVEMENT_ANIMATION_DURATION,
                );
                return () => clearTimeout(timer);
            }
        }, [isMovementAnimating]);

        useEffect(() => {
            const wrapper = wrapperRef.current;
            const container = containerRef.current;

            if (wrapper) {
                setWrapperWidth(wrapper.clientWidth);
            }

            if (container) {
                setContainerWidth(container.getBoundingClientRect().width);
            }
        }, [children]);

        useEffect(() => {
            const wrapper = wrapperRef.current;

            if (!wrapper) {
                return;
            }

            const handleWheel = (evt: WheelEvent) => {
                evt.preventDefault();
                changeOffsetX(evt.deltaX, false);
            };

            wrapper.addEventListener("wheel", handleWheel, { passive: false });

            return () => wrapper.removeEventListener("wheel", handleWheel);
        }, [changeOffsetX]);

        const handleLeftClick = () => {
            if (onLeftClick) {
                onLeftClick();
            } else {
                changeOffsetX(-offset);
            }
        };

        const handleRightClick = () => {
            if (onRightClick) {
                onRightClick();
            } else {
                changeOffsetX(offset);
            }
        };

        const containerStyle: CSSProperties = {
            transform: `translateX(${-offsetX}px)`,
            transition: isMovementAnimating
                ? `transform ${MOVEMENT_ANIMATION_DURATION}ms ease`
                : "none",
        };

        const wrapperClassName = cn(
            classes["wrapper"],
            classes[`__view-${view}`],
            customColorsStyles,
        );

        const containerClassName = cn(
            classes["container"],
            classes[`__view-${view}`],
        );

        return (
            <div
                ref={wrapperRef}
                data-tsid={props["data-tsid"]}
                className={wrapperClassName}
            >
                {offsetX > 0 && (
                    <button
                        type="button"
                        className={classes["scroll-button-left"]}
                        onClick={handleLeftClick}
                        aria-label={labelLeft}
                    >
                        <ScrollButtonCustom
                            className={classes["scroll-button-icon"]}
                        />
                    </button>
                )}
                <div
                    ref={containerRef}
                    className={containerClassName}
                    style={containerStyle}
                >
                    {children}
                </div>
                {offsetX < maxContainerOffset && (
                    <button
                        type="button"
                        className={classes["scroll-button-right"]}
                        onClick={handleRightClick}
                        aria-label={labelRight}
                    >
                        <ScrollButtonCustom
                            className={classes["scroll-button-icon"]}
                        />
                    </button>
                )}
            </div>
        );
    },
);

ScrollContainer.displayName = "ScrollContainer";

export default ScrollContainer;
